/**
 * The formula editors' "Suggested" operands (z8nrz7deww): of the fields the
 * picker offers, the few most likely to go with the operands already placed.
 *
 * Signal: saved formulas. Two fields "co-occur" when they are operands of the
 * same saved formula. A field is identified by its Field Library origin when it
 * has one (so "Weight" and "Height" pairing in one template suggests the pair in
 * another, a brand-new form included), else by its own `handle.fieldId`.
 *
 * Ranking: more formulas shared with a placed operand first; then fields on this
 * form ahead of earlier forms' (the step being built); then by label. A field
 * qualifies only through co-occurrence, so with nothing placed there is nothing
 * to suggest and no group shows. Pure, so the ranking is tested on its own.
 */
import { extractFormulaRefs } from './formula'
import { crossFormKey, parseCrossFormRef } from './crossForm'

/** A saved form, flattened: its handle and its fields. */
export interface CorpusForm {
  handle: string
  fields: { id: string; libraryFieldId?: string; formula?: string | null }[]
}

export interface FormulaCorpus {
  /** Each saved formula: its owner (`handle.fieldId`) and its operands' identities. */
  formulas: { owner: string; operands: Set<string> }[]
  /** `handle.fieldId` → identity (library origin, else the key itself). */
  identityOf: Map<string, string>
}

export interface Suggestion {
  refKey: string
  label: string
  /** Why: the signal that put it here, e.g. "used in 3 formulas with Weight (kg)". */
  reason: string
}

export const MAX_SUGGESTIONS = 5

export function buildCorpus(forms: CorpusForm[]): FormulaCorpus {
  const identityOf = new Map<string, string>()
  for (const form of forms) {
    for (const f of form.fields) {
      const key = crossFormKey(form.handle, f.id)
      identityOf.set(key, f.libraryFieldId ? `lib:${f.libraryFieldId}` : key)
    }
  }
  const formulas: FormulaCorpus['formulas'] = []
  for (const form of forms) {
    for (const f of form.fields) {
      if (!f.formula) continue
      const operands = new Set<string>()
      for (const ref of extractFormulaRefs(f.formula)) {
        // A local ref is a field on the same form; a cross ref names its form.
        const key = parseCrossFormRef(ref) ? ref : crossFormKey(form.handle, ref)
        operands.add(identityOf.get(key) ?? key)
      }
      if (operands.size >= 2) formulas.push({ owner: crossFormKey(form.handle, f.id), operands })
    }
  }
  return { formulas, identityOf }
}

/**
 * The suggestions for one picker: `candidates` are what it offers (identity
 * resolved by the caller), `placed` the identities already in the formula,
 * `exclude` the formula being edited (its saved copy must not suggest itself).
 */
export function suggestOperands({
  candidates,
  placed,
  corpus,
  exclude,
  labelOf,
}: {
  candidates: { refKey: string; label: string; identity: string; onThisForm: boolean }[]
  placed: string[]
  corpus: FormulaCorpus
  exclude?: string
  /** A placed operand's label, for the reason line. */
  labelOf: (identity: string) => string
}): Suggestion[] {
  const placedSet = new Set(placed)
  if (placedSet.size === 0) return []
  const formulas = corpus.formulas.filter(f => f.owner !== exclude && [...placedSet].some(p => f.operands.has(p)))
  if (formulas.length === 0) return []

  const seen = new Set<string>()
  const scored: { c: (typeof candidates)[number]; count: number; partner: string }[] = []
  for (const c of candidates) {
    // Once each, and never an operand that is already in the formula.
    if (seen.has(c.identity) || placedSet.has(c.identity)) continue
    seen.add(c.identity)
    const shared = formulas.filter(f => f.operands.has(c.identity))
    if (shared.length === 0) continue
    // The placed operand it is paired with most (ties: by label).
    let partner = ''
    let best = 0
    for (const p of [...placedSet].sort((a, b) => labelOf(a).localeCompare(labelOf(b)))) {
      const n = shared.filter(f => f.operands.has(p)).length
      if (n > best) {
        best = n
        partner = p
      }
    }
    scored.push({ c, count: shared.length, partner })
  }
  scored.sort(
    (a, b) =>
      b.count - a.count ||
      Number(b.c.onThisForm) - Number(a.c.onThisForm) ||
      a.c.label.localeCompare(b.c.label) ||
      a.c.refKey.localeCompare(b.c.refKey),
  )
  return scored.slice(0, MAX_SUGGESTIONS).map(({ c, count, partner }) => ({
    refKey: c.refKey,
    label: c.label,
    reason: `used in ${count} ${count === 1 ? 'formula' : 'formulas'} with ${labelOf(partner)}${c.onThisForm ? ' · on this form' : ''}`,
  }))
}
